"use client";

import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

import { createAudio, type CreateAudioReturn } from "@/app/_blocks/BlockInteractCreateOmnibox/createAudio";
import { isXiApiUrl } from "@/app/_api/elevenlabs/fetch";
import { getCaptchaToken } from "@/app/_providers/hcaptcha/core";
import { logger } from "@utilities/logger";

type PlayState = "idle" | "loading" | "playing";

interface AudioPlayerContextValue {
  activeId: string | null;
  playState: PlayState;
  analyser: AnalyserNode | null;
  currentTime: number;
  duration: number;
  handlePlay: (item: { id: string; audioUrl: string; loop?: boolean }) => void;
  togglePlayPause: () => void;
  stopActive: () => void;
}

const AudioPlayerContext = createContext<AudioPlayerContextValue | null>(null);

const NOOP_CONTEXT: AudioPlayerContextValue = {
  activeId: null,
  playState: "idle",
  analyser: null,
  currentTime: 0,
  duration: 0,
  handlePlay: () => {},
  togglePlayPause: () => {},
  stopActive: () => {},
};

export function useAudioPlayer(): AudioPlayerContextValue {
  return useContext(AudioPlayerContext) ?? NOOP_CONTEXT;
}

export function AudioPlayerProvider({ children }: { children: React.ReactNode }) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [playState, setPlayState] = useState<PlayState>("idle");
  const [analyser, setAnalyser] = useState<AnalyserNode | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  // Incremented each time a new audio instance is placed in the map, so that
  // the timeupdate/durationchange subscription effect re-runs even when activeId
  // doesn't change (e.g. Xi API URLs that delay audio creation via captcha fetch).
  const [audioVersion, setAudioVersion] = useState(0);

  const audioMapRef = useRef<Map<string, CreateAudioReturn>>(new Map());
  const analyserMapRef = useRef<Map<string, AnalyserNode | null>>(new Map());
  const activeIdRef = useRef<string | null>(null);
  const playStateRef = useRef<PlayState>("idle");

  // Subscribe currentTime/duration to the active audio element
  useEffect(() => {
    if (!activeId) {
      setCurrentTime(0);
      setDuration(0);
      return;
    }
    const audio = audioMapRef.current.get(activeId);
    if (!audio) return;
    const el = audio.getAudio();
    if (!el) return;

    const onTimeUpdate = () => setCurrentTime(el.currentTime);
    const onDurationChange = () => setDuration(isFinite(el.duration) ? el.duration : 0);

    el.addEventListener("timeupdate", onTimeUpdate);
    el.addEventListener("durationchange", onDurationChange);
    // Capture current values immediately
    setCurrentTime(el.currentTime);
    setDuration(isFinite(el.duration) ? el.duration : 0);

    return () => {
      el.removeEventListener("timeupdate", onTimeUpdate);
      el.removeEventListener("durationchange", onDurationChange);
    };
  }, [activeId, audioVersion]);

  const stopActive = useCallback(() => {
    const audioMap = audioMapRef.current;
    const id = activeIdRef.current;
    if (id) {
      audioMap.get(id)?.pause();
    }
    playStateRef.current = "idle";
    setPlayState("idle");
    setActiveId(null);
    setAnalyser(null);
    // currentTime/duration reset is handled by the activeId useEffect
    activeIdRef.current = null;
  }, []);

  const togglePlayPause = useCallback(() => {
    const audioMap = audioMapRef.current;
    const id = activeIdRef.current;
    if (!id) return;
    const audio = audioMap.get(id);
    if (!audio) return;

    if (playStateRef.current === "playing") {
      // Pause but keep activeId so the player bar stays visible and resumable
      audio.pause();
      playStateRef.current = "idle";
      setPlayState("idle");
    } else {
      // Resume from paused state
      playStateRef.current = "loading";
      setPlayState("loading");
      audio
        .play()
        .then(() => {
          if (activeIdRef.current === id) {
            playStateRef.current = "playing";
            setPlayState("playing");
            setAnalyser(analyserMapRef.current.get(id) ?? null);
          }
        })
        .catch((err: unknown) => {
          logger.warn("[AudioPlayer] Playback resume failed", { id, err });
          if (activeIdRef.current === id) {
            playStateRef.current = "idle";
            setPlayState("idle");
            setActiveId(null);
            setAnalyser(null);
            activeIdRef.current = null;
          }
        });
    }
  }, []);

  const handlePlay = useCallback(({ id, audioUrl, loop }: { id: string; audioUrl: string; loop?: boolean }) => {
    const audioMap = audioMapRef.current;
    const analyserMap = analyserMapRef.current;
    const currentActiveId = activeIdRef.current;

    // Toggle: clicking the active item stops it entirely (deselects from list)
    if (currentActiveId === id) {
      audioMap.get(id)?.pause();
      playStateRef.current = "idle";
      setPlayState("idle");
      setActiveId(null);
      setAnalyser(null);
      activeIdRef.current = null;
      return;
    }

    // Pause whatever was playing before
    if (currentActiveId) {
      audioMap.get(currentActiveId)?.pause();
    }

    setActiveId(id);
    playStateRef.current = "loading";
    setPlayState("loading");
    setAnalyser(null);
    activeIdRef.current = id;

    const resolveAndPlay = async () => {
      try {
        let resolvedUrl = audioUrl;
        if (isXiApiUrl(audioUrl)) {
          const token = await getCaptchaToken({ behaviourType: "sound_effects" });
          const u = new URL(audioUrl);
          u.searchParams.set("hcaptcha_token", token);
          resolvedUrl = u.toString();
        }

        // if the active ID changed while we were awaiting the token, abort
        if (activeIdRef.current !== id) return;

        // For XI API URLs the token is short-lived, so always recreate the audio
        // instance on each play to avoid using a stale token from the cache.
        const existingAudio = audioMap.get(id);
        if (existingAudio && isXiApiUrl(audioUrl)) {
          existingAudio.cleanup();
          audioMap.delete(id);
          analyserMap.delete(id);
        }

        let audio = audioMap.get(id);
        if (!audio) {
          audio = createAudio({
            src: resolvedUrl,
            loop,
            fade: false,
            onLoaded: ({ analyser: a }) => {
              analyserMap.set(id, a ?? null);
            },
            onEnded: () => {
              if (activeIdRef.current === id) {
                playStateRef.current = "idle";
                setPlayState("idle");
                setActiveId(null);
                setAnalyser(null);
                activeIdRef.current = null;
              }
            },
          });
          audioMap.set(id, audio);
          audio.load();
          // Signal the subscription effect to re-run now that audio is in the map.
          // Necessary for Xi API URLs where captcha token fetch delays audio creation
          // past the point where the effect first ran (and exited early).
          setAudioVersion(v => v + 1);
        }

        await audio.play();
        if (activeIdRef.current === id) {
          playStateRef.current = "playing";
          setPlayState("playing");
          setAnalyser(analyserMap.get(id) ?? null);
        }
      } catch (err: unknown) {
        logger.warn("[AudioPlayer] Playback blocked or failed", { id, err });
        if (activeIdRef.current === id) {
          playStateRef.current = "idle";
          setPlayState("idle");
          setActiveId(null);
          setAnalyser(null);
          activeIdRef.current = null;
        }
      }
    };

    resolveAndPlay();
  }, []);

  useEffect(() => {
    const audioMap = audioMapRef.current;
    return () => {
      audioMap.forEach(a => a.cleanup());
      audioMap.clear();
      analyserMapRef.current.clear();
    };
  }, []);

  return (
    <AudioPlayerContext.Provider
      value={{ activeId, playState, analyser, currentTime, duration, handlePlay, togglePlayPause, stopActive }}
    >
      {children}
    </AudioPlayerContext.Provider>
  );
}

export { AudioPlayerContext };
export type { AudioPlayerContextValue };
